import React, { ReactNode, ButtonHTMLAttributes } from 'react';
import { Button as PrimeButton } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { AppTheme } from '../../AppTheme';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'text';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  icon?: string;
  iconPos?: 'left' | 'right';
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  className = '',
  icon,
  iconPos = 'left',
  ...props
}) => {
  // Determine the styling based on variant
  const getButtonClass = () => {
    const baseClass = 'font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
    
    // Use basic classes without color (colors will be applied via style prop)
    const variantClasses = {
      primary: `text-white disabled:cursor-not-allowed`,
      secondary: `bg-white border disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed`,
      danger: `text-white disabled:bg-red-300 disabled:text-white disabled:cursor-not-allowed`,
      text: `bg-transparent disabled:text-gray-400 disabled:bg-transparent disabled:cursor-not-allowed`
    };
    
    const sizeClasses = {
      sm: 'text-xs px-3 py-2 rounded-md',
      md: 'text-sm px-3 py-2 rounded-md',
      lg: 'text-base px-3 py-2 rounded-md'
    };
    
    return classNames(
      // baseClass,
      // variantClasses[variant],
       sizeClasses[size],
      className
    );
  };

  // Map our variants to PrimeReact severity
  const getSeverity = () => {
    const severityMap: Record<ButtonVariant, "secondary" | "danger" | "info" | undefined> = {
      primary: undefined,
      secondary: 'secondary',
      danger: 'danger',
      text: 'info'
    };
    
    return severityMap[variant];
  };

  // Get the variant-specific styles based on AppTheme
  const getVariantStyles = () => {
    const styles: React.CSSProperties = {
      borderRadius: AppTheme.borderRadius.sm,
      height: '40px',
      transition: 'all 0.2s ease',
    };

    switch (variant) {
      case 'primary':
        styles.backgroundColor = props.disabled 
          ? `${AppTheme.colors.primary.default}80` // Adding 80 for transparency when disabled
          : AppTheme.colors.primary.default;
        styles.color = AppTheme.colors.white;
        break;
      case 'secondary':
        styles.backgroundColor = AppTheme.colors.white;
        styles.color = AppTheme.colors.primary.default;
        styles.borderColor = AppTheme.colors.primary.default;
        styles.borderWidth = '1px';
        styles.borderStyle = 'solid';
        if (props.disabled) {
          styles.color = AppTheme.colors.gray.medium;
          styles.borderColor = AppTheme.colors.gray.medium;
          styles.backgroundColor = AppTheme.colors.gray.light;
        }
        break;
      case 'danger':
        styles.backgroundColor = props.disabled 
          ? `${AppTheme.colors.semantic.danger}80` // Adding 80 for transparency when disabled
          : AppTheme.colors.semantic.danger;
        styles.color = AppTheme.colors.white;
        break;
      case 'text':
        styles.backgroundColor = 'transparent';
        styles.color = props.disabled
          ? AppTheme.colors.gray.medium
          : AppTheme.colors.primary.default;
        break;
    }

    return styles;
  };

  return (
    <PrimeButton
      className={getButtonClass()}
      loading={isLoading}
      disabled={props.disabled || isLoading}
      icon={icon}
      iconPos={iconPos}
      label={children as string}
      severity={getSeverity()}
      // style={getVariantStyles()}
      {...props}
    />
  );
};